import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,

  Card,
  CardContent,

  Checkbox,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Fade,
  Avatar,
  Divider,
  FormControlLabel,
  Tooltip,
  IconButton,
  Badge
} from '@mui/material';
import {
  Article as ArticleIcon,
  Search as SearchIcon,
  Psychology as PsychologyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AutoAwesome as AutoAwesomeIcon,
  SelectAll as SelectAllIcon,
  ClearAll as DeselectAllIcon,
  ViewColumn as ViewColumnIcon,
  ViewStream as ViewStreamIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import MarkdownRenderer from '../Common/MarkdownRenderer';

const OrchestrationResults = ({
  orchestrationData,
  loading,
  onSynthesize,
  synthesizing,
  synthesisData
}) => {
  const theme = useTheme();

  const [selectedResults, setSelectedResults] = useState(new Set());
  const [mounted, setMounted] = useState(false);
  const [expandedView, setExpandedView] = useState(false);
  const [expandedCards, setExpandedCards] = useState(new Set());

  useEffect(() => {
    if (orchestrationData) {
      setMounted(true);
    }
  }, [orchestrationData]);



  const handleResultSelection = (resultId, isSelected) => {
    const newSelected = new Set(selectedResults);
    if (isSelected) {
      newSelected.add(resultId);
    } else {
      newSelected.delete(resultId);
    }
    setSelectedResults(newSelected);
  };

  const handleSelectAll = (serviceResults) => {
    const newSelected = new Set(selectedResults);
    serviceResults.forEach(result => {
      newSelected.add(result.id);
    });
    setSelectedResults(newSelected);
  };

  const handleDeselectAll = (serviceResults) => {
    const newSelected = new Set(selectedResults);
    serviceResults.forEach(result => {
      newSelected.delete(result.id);
    });
    setSelectedResults(newSelected);
  };

  const handleCardExpansion = (cardId) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(cardId)) {
      newExpanded.delete(cardId);
    } else {
      newExpanded.add(cardId);
    }
    setExpandedCards(newExpanded);
  };

  const handleSynthesize = () => {
    if (selectedResults.size === 0) return;

    // Collect selected result objects
    const selectedResultObjects = [];

    if (orchestrationData?.results) {
      Object.entries(orchestrationData.results).forEach(([serviceName, serviceData]) => {
        // Check for selected generated answers
        if (selectedResults.has(`${serviceName}_answer`)) {
          const generatedAnswer = serviceData.metadata?.generated_answer ||
                                 (serviceName === 'core_reasoning' && serviceData.results?.[0]?.content);

          if (generatedAnswer) {
            selectedResultObjects.push({
              id: `${serviceName}_answer`,
              source: serviceName,
              title: `Generated Answer from ${serviceName}`,
              content: generatedAnswer,
              metadata: { type: 'generated_answer', service: serviceName }
            });
          }
        }

        // Check for selected individual results
        if (serviceData.results) {
          serviceData.results.forEach(result => {
            if (selectedResults.has(result.id)) {
              selectedResultObjects.push(result);
            }
          });
        }
      });
    }

    onSynthesize(selectedResultObjects, orchestrationData.query);
  };

  const getServiceIcon = (serviceName) => {
    switch (serviceName) {
      case 'rag':
        return <ArticleIcon />;
      case 'web_search':
        return <SearchIcon />;
      case 'core_reasoning':
        return <PsychologyIcon />;
      default:
        return <AutoAwesomeIcon />;
    }
  };

  const getServiceDisplayName = (serviceName) => {
    switch (serviceName) {
      case 'rag':
        return 'Internal Documents';
      case 'web_search':
        return 'Web Search';
      case 'core_reasoning':
        return 'Core Reasoning';
      default:
        return serviceName;
    }
  };

  // Render expanded service card
  const renderExpandedServiceCard = (serviceName, serviceData) => {
    if (!serviceData || !serviceData.results) {
      return (
        <Alert severity="warning">
          No results available for {getServiceDisplayName(serviceName)}
        </Alert>
      );
    }

    if (serviceData.status === 'error') {
      return (
        <Alert severity="error">
          Error in {getServiceDisplayName(serviceName)}: {serviceData.error_message}
        </Alert>
      );
    }

    const results = serviceData.results;
    const hasGeneratedAnswer = serviceData.metadata?.generated_answer ||
                              (serviceName === 'core_reasoning' && results.length > 0);
    const generatedAnswer = serviceData.metadata?.generated_answer ||
                           (serviceName === 'core_reasoning' ? results[0]?.content : null);

    return (
      <Card
        variant="outlined"
        sx={{
          border: `2px solid ${theme.palette.primary.light}`,
          borderRadius: 3,
        }}
      >
        {/* Expanded Header */}
        <Box sx={{
          p: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {getServiceIcon(serviceName)}
              <Box sx={{ ml: 2 }}>
                <Typography variant="h5" fontWeight="bold">
                  {getServiceDisplayName(serviceName)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Success"
                    size="small"
                    color="success"
                  />
                  <Chip
                    label={`${results.length} results`}
                    size="small"
                  />
                </Box>
              </Box>
            </Box>

            <Box>
              <Tooltip title="Select All">
                <IconButton
                  size="large"
                  onClick={() => handleSelectAll(results)}
                  color="primary"
                >
                  <SelectAllIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Deselect All">
                <IconButton
                  size="large"
                  onClick={() => handleDeselectAll(results)}
                >
                  <DeselectAllIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Expanded Content */}
        <Box sx={{ p: 3 }}>
          {/* Generated Answer Section */}
          {hasGeneratedAnswer && (
            <Card
              variant="outlined"
              sx={{
                mb: 3,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)',
                border: `2px solid ${theme.palette.success.light}`,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedResults.has(`${serviceName}_answer`)}
                        onChange={(e) => handleResultSelection(`${serviceName}_answer`, e.target.checked)}
                        color="success"
                        size="large"
                      />
                    }
                    label=""
                    sx={{ mt: 0 }}
                  />

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="success.main">
                      📝 Generated Answer
                    </Typography>

                    <Box
                      sx={{
                        mb: 2,
                        backgroundColor: theme.palette.background.paper,
                        p: 3,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    >
                      <MarkdownRenderer
                        content={generatedAnswer}
                        variant="body1"
                        sx={{ lineHeight: 1.7 }}
                      />
                    </Box>

                    <Chip
                      label="Generated Answer"
                      size="medium"
                      color="success"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Individual Results Section */}
          {results.length > 0 && serviceName !== 'core_reasoning' && (
            <>
              <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'text.secondary' }}>
                📚 Source Documents:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {results.map((result, index) => (
                  <Card
                    key={result.id}
                    variant="outlined"
                    sx={{
                      transition: 'all 0.2s ease',
                      border: selectedResults.has(result.id)
                        ? `2px solid ${theme.palette.primary.main}`
                        : `1px solid ${theme.palette.divider}`,
                      '&:hover': {
                        boxShadow: 2,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedResults.has(result.id)}
                              onChange={(e) => handleResultSelection(result.id, e.target.checked)}
                              color="primary"
                              size="large"
                            />
                          }
                          label=""
                          sx={{ mt: 0 }}
                        />

                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight="bold" gutterBottom>
                            {result.title}
                          </Typography>

                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{
                              mb: 2,
                              lineHeight: 1.6
                            }}
                          >
                            {result.content}
                          </Typography>

                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {result.score && (
                              <Chip
                                label={`Relevance: ${(result.score * 100).toFixed(0)}%`}
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                            )}
                            <Chip
                              label={result.source}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Card>
    );
  };

  // Render expanded synthesis card
  const renderExpandedSynthesisCard = () => {
    return (
      <Card
        variant="outlined"
        sx={{
          border: `2px solid ${theme.palette.success.main}`,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)',
          borderRadius: 3,
        }}
      >
        {/* Synthesis Header */}
        <Box sx={{
          p: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AutoAwesomeIcon sx={{ color: 'success.main', fontSize: 32, mr: 2 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                Synthesized Answer
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Generated"
                  size="small"
                  color="success"
                />
                <Chip
                  label={`From ${synthesisData.sources_used?.length || 0} sources`}
                  size="small"
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Synthesis Content */}
        <Box sx={{ p: 3 }}>
          <Card
            variant="outlined"
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.success.light}`,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom color="success.main">
                🎯 Final Answer
              </Typography>

              <Box sx={{ mb: 3 }}>
                <MarkdownRenderer
                  content={synthesisData.synthesized_answer}
                  variant="body1"
                  sx={{ lineHeight: 1.7 }}
                />
              </Box>

              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                Sources Used:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {synthesisData.sources_used?.map((source, index) => (
                  <Chip
                    key={index}
                    label={source}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Card>
    );
  };

  const renderServiceResults = (serviceName, serviceData) => {
    if (!serviceData || !serviceData.results) {
      return (
        <Alert severity="warning">
          No results available
        </Alert>
      );
    }

    if (serviceData.status === 'error') {
      return (
        <Alert severity="error">
          {serviceData.error_message}
        </Alert>
      );
    }

    const results = serviceData.results;

    // Show generated answer if available (for RAG with reasoning or Core Reasoning)
    const hasGeneratedAnswer = serviceData.metadata?.generated_answer ||
                              (serviceName === 'core_reasoning' && results.length > 0);
    const generatedAnswer = serviceData.metadata?.generated_answer ||
                           (serviceName === 'core_reasoning' ? results[0]?.content : null);

    return (
      <Box>

        {/* Generated Answer Section */}
        {hasGeneratedAnswer && (
          <Card
            variant="outlined"
            sx={{
              mb: 3,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)',
              border: `2px solid ${theme.palette.success.light}`,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedResults.has(`${serviceName}_answer`)}
                      onChange={(e) => handleResultSelection(`${serviceName}_answer`, e.target.checked)}
                      color="success"
                    />
                  }
                  label=""
                  sx={{ mt: 0 }}
                />

                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="success.main">
                      📝 Generated Answer
                    </Typography>

                    <Tooltip title={expandedCards.has(`${serviceName}_answer`) ? "Collapse" : "Expand"}>
                      <IconButton
                        size="small"
                        onClick={() => handleCardExpansion(`${serviceName}_answer`)}
                        sx={{
                          minWidth: 'auto',
                          p: 0.5,
                          color: 'success.main'
                        }}
                      >
                        {expandedCards.has(`${serviceName}_answer`) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box
                    sx={{
                      mb: 2,
                      backgroundColor: theme.palette.background.paper,
                      p: 1.5,
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      ...(expandedCards.has(`${serviceName}_answer`) ? {
                        // Expanded state
                        maxHeight: 'none'
                      } : {
                        // Collapsed state
                        maxHeight: '120px',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 5,
                        WebkitBoxOrient: 'vertical',
                        textOverflow: 'ellipsis'
                      })
                    }}
                  >
                    <MarkdownRenderer
                      content={generatedAnswer}
                      variant="body2"
                      sx={{ lineHeight: 1.5 }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip
                      label="Generated Answer"
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                    {expandedCards.has(`${serviceName}_answer`) && (
                      <Typography variant="caption" color="success.main">
                        Full answer shown
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Individual Results Section */}
        {results.length > 0 && serviceName !== 'core_reasoning' && (
          <>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, color: 'text.secondary' }}>
              📚 Source Documents:
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: '300px', overflow: 'auto' }}>
              {results.map((result, index) => (
                <Card
                  key={result.id}
                  variant="outlined"
                  sx={{
                    transition: 'all 0.2s ease',
                    border: selectedResults.has(result.id)
                      ? `2px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.divider}`,
                    '&:hover': {
                      boxShadow: 2,
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedResults.has(result.id)}
                            onChange={(e) => handleResultSelection(result.id, e.target.checked)}
                            color="primary"
                            size="small"
                          />
                        }
                        label=""
                        sx={{ mt: 0 }}
                      />

                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            sx={{
                              flex: 1,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              mr: 1
                            }}
                          >
                            {result.title}
                          </Typography>

                          <Tooltip title={expandedCards.has(result.id) ? "Collapse" : "Expand"}>
                            <IconButton
                              size="small"
                              onClick={() => handleCardExpansion(result.id)}
                              sx={{
                                minWidth: 'auto',
                                p: 0.5,
                                color: 'text.secondary'
                              }}
                            >
                              {expandedCards.has(result.id) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 1,
                            fontSize: '0.85rem',
                            lineHeight: 1.4,
                            ...(expandedCards.has(result.id) ? {
                              // Expanded state - show full content
                              whiteSpace: 'pre-wrap'
                            } : {
                              // Collapsed state - truncate
                              display: '-webkit-box',
                              WebkitLineClamp: 4,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            })
                          }}
                        >
                          {result.content}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                          {result.score && (
                            <Chip
                              label={`${(result.score * 100).toFixed(0)}%`}
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: '20px' }}
                            />
                          )}
                          {expandedCards.has(result.id) && (
                            <Typography variant="caption" color="text.secondary">
                              Full content shown
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Searching across multiple sources...
        </Typography>
      </Paper>
    );
  }

  if (!orchestrationData) {
    return null;
  }

  const availableServices = orchestrationData.services_used || [];
  const hasErrors = Object.keys(orchestrationData.errors || {}).length > 0;

  return (
    <Fade in={mounted} timeout={500}>
      <Paper
        elevation={2}
        sx={{
          mt: 3,
          borderRadius: 3,
          overflow: 'hidden',
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.5)' : 'rgba(248, 249, 250, 0.8)',
          backdropFilter: 'blur(8px)',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                mr: 2,
                width: 36,
                height: 36,
              }}
            >
              <AutoAwesomeIcon fontSize="small" />
            </Avatar>
            <Typography variant="h6" fontWeight="bold">
              Multi-Source Search Results
            </Typography>
            {hasErrors && (
              <Chip 
                icon={<ErrorIcon />}
                label="Some errors occurred" 
                color="warning" 
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </Box>

          {/* Status and controls */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Query: "{orchestrationData.query}"
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* View Toggle */}
              <Tooltip title={expandedView ? "Switch to Column View" : "Switch to Expanded View"}>
                <IconButton
                  onClick={() => setExpandedView(!expandedView)}
                  color="primary"
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                  }}
                >
                  {expandedView ? <ViewColumnIcon /> : <ViewStreamIcon />}
                </IconButton>
              </Tooltip>

              {/* Synthesis Button */}
              <Button
                variant="contained"
                startIcon={<AutoAwesomeIcon />}
                onClick={handleSynthesize}
                disabled={selectedResults.size === 0 || synthesizing}
                sx={{ borderRadius: 2 }}
              >
                {synthesizing ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Synthesizing...
                  </>
                ) : (
                  `Generate Final Answer (${selectedResults.size} selected)`
                )}
              </Button>
            </Box>
          </Box>

          <Divider />
        </Box>

        {/* Service Results Layout */}
        <Box sx={{ p: 3 }}>
          {expandedView ? (
            /* Expanded View - Vertical Stack */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {availableServices.map((serviceName) => {
                const serviceData = orchestrationData.results[serviceName];
                const hasError = orchestrationData.errors[serviceName];

                return (
                  <Box key={serviceName}>
                    {hasError ? (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        Error in {getServiceDisplayName(serviceName)}: {orchestrationData.errors[serviceName]}
                      </Alert>
                    ) : (
                      renderExpandedServiceCard(serviceName, serviceData)
                    )}
                  </Box>
                );
              })}

              {/* Synthesized Answer in Expanded View */}
              {synthesisData && (
                <Box>
                  {renderExpandedSynthesisCard()}
                </Box>
              )}
            </Box>
          ) : (
            /* Column View - Grid Layout */
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: synthesisData ? '1fr 1fr' : availableServices.length === 2 ? '1fr 1fr' : '1fr',
                lg: synthesisData ?
                  (availableServices.length === 3 ? '1fr 1fr 1fr 1fr' : availableServices.length === 2 ? '1fr 1fr 1fr' : '1fr 1fr') :
                  (availableServices.length === 3 ? '1fr 1fr 1fr' : availableServices.length === 2 ? '1fr 1fr' : '1fr')
              },
              gap: 3
            }}>
            {availableServices.map((serviceName) => {
              const serviceData = orchestrationData.results[serviceName];
              const hasError = orchestrationData.errors[serviceName];
              const resultCount = serviceData?.results?.length || 0;

              return (
                <Card
                  key={serviceName}
                  variant="outlined"
                  sx={{
                    height: 'fit-content',
                    border: hasError ? `2px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.divider}`,
                    backgroundColor: hasError
                      ? 'rgba(244, 67, 54, 0.05)'
                      : theme.palette.background.paper,
                  }}
                >
                  {/* Service Header */}
                  <Box sx={{
                    p: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: hasError
                      ? 'rgba(244, 67, 54, 0.1)'
                      : theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getServiceIcon(serviceName)}
                        <Box sx={{ ml: 1 }}>
                          <Typography variant="h6" fontWeight="bold">
                            {getServiceDisplayName(serviceName)}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            {hasError ? (
                              <Chip
                                icon={<ErrorIcon />}
                                label="Error"
                                size="small"
                                color="error"
                              />
                            ) : (
                              <>
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label="Success"
                                  size="small"
                                  color="success"
                                />
                                <Chip
                                  label={`${resultCount} results`}
                                  size="small"
                                />
                              </>
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {!hasError && resultCount > 0 && (
                        <Box>
                          <Tooltip title="Select All">
                            <IconButton
                              size="small"
                              onClick={() => handleSelectAll(serviceData.results)}
                              color="primary"
                            >
                              <SelectAllIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deselect All">
                            <IconButton
                              size="small"
                              onClick={() => handleDeselectAll(serviceData.results)}
                            >
                              <DeselectAllIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Service Content */}
                  <Box sx={{ p: 2 }}>
                    {hasError ? (
                      <Alert severity="error">
                        {orchestrationData.errors[serviceName]}
                      </Alert>
                    ) : (
                      renderServiceResults(serviceName, serviceData)
                    )}
                  </Box>
                </Card>
              );
            })}

            {/* Synthesized Answer Column */}
            {synthesisData && (
              <Card
                variant="outlined"
                sx={{
                  height: 'fit-content',
                  border: `2px solid ${theme.palette.success.main}`,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)',
                }}
              >
                {/* Synthesis Header */}
                <Box sx={{
                  p: 2,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AutoAwesomeIcon sx={{ color: 'success.main' }} />
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="h6" fontWeight="bold" color="success.main">
                          Synthesized Answer
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Generated"
                            size="small"
                            color="success"
                          />
                          <Chip
                            label={`From ${synthesisData.sources_used?.length || 0} sources`}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Synthesis Content */}
                <Box sx={{ p: 2 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.success.light}`,
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="success.main">
                        🎯 Final Answer
                      </Typography>

                      <Box
                        sx={{
                          maxHeight: '300px',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 12,
                          WebkitBoxOrient: 'vertical',
                          textOverflow: 'ellipsis',
                          mb: 2
                        }}
                      >
                        <MarkdownRenderer
                          content={synthesisData.synthesized_answer}
                          variant="body2"
                          sx={{ lineHeight: 1.5 }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {synthesisData.sources_used?.map((source, index) => (
                          <Chip
                            key={index}
                            label={source}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Card>
            )}
            </Box>
          )}
        </Box>
      </Paper>
    </Fade>
  );
};

export default OrchestrationResults;
